<template>
  <div
    class="FormDate"
    :class="{
      'focused': focused,
      'invalid': isInvalid,
      'disabled': disabled
    }"
    @keydown.capture="moveSelector"
  >
    <div
      v-for="(segment, key) in orderByFormat"
      :key="key"
    >
      <div
        class="FormDate_segment"
        :class="'FormDate_segment--' + dateInputs[segment].ref"
        v-if="showingSegment(segment)"
      >
        <input
          class="FormDate__input"
          :class="'FormDate__input--' + dateInputs[segment].ref"
          :ref="dateInputs[segment].ref"
          :value="dateInputs[segment].value"
          type="number"
          :minlength="0"
          :maxLength="dateInputs[segment].blurPadding"
          :placeholder="segment"
          :disabled="disabled"
          @keyup.up="addToSegment(segment)"
          @keyup.down="subtractSegment(segment)"
          @input="setSegmentValue"
          @focus="focusSegment(segment)"
          @blur="onBlur"
        >

        <span
          class="FormDate__divider"
          :class="{
            'empty': isFullDate
          }"
          v-if="key < 2"
          v-text="'-'"
        >
        </span>
      </div>
    </div>

    <button
      class="clear-button small-white"
      :disabled="disabled"
      @click="clearInput"
    >
      <p4a-icon
        class="null-icon"
        :icon="'close-modal'"
        :color="disabled ? '#dddddd' : '#212121'"
      />
    </button>
  </div>
</template>

<script>
  import Vue from 'vue'
  import _ from 'lodash'
  import * as moment from 'moment'

  export default {
    name: 'DateInput',
    data () {
      return {
        showDebugLogs: false,
        dateInputs: {
          dd: { value: '', ref: 'day', blurPadding: 2 },
          mm: { value: '', ref: 'month', blurPadding: 2 },
          yyyy: { value: '', ref: 'year', blurPadding: 4 }
        },
        momentArguments: {
          dd: '',
          mm: '',
          yyyy: ''
        },
        focused: false,
        activeSegment: null,
        timeOut: null
      }
    },
    props: {
      value: {
        type: Object,
        required: true
      },
      format: {
        default: 'dd-mm-yyyy',
        type: String
      },
      showDate: {
        default: function () {
          return {
            'day': true,
            'month': true,
            'year': true
          }
        },
        type: Object
      },
      isNullable: {
        default: true,
        type: Boolean
      },
      disabled: {
        default: false,
        type: Boolean
      }
    },
    computed: {
      orderByFormat () {
        // Ensure format matches section names
        return _.toLower(this.format).split('-')
      },
      daysInMonthsOfYear () {
        let self = this
        let result = []

        let yearDate = moment().set({
          year: Number(self.momentArguments.yyyy),
          months: 0,
          date: 1
        })

        _.times(12, function () {
          // Aggregate days for each month
          result.push(moment(yearDate).daysInMonth())

          // Increase current date by one month
          yearDate = moment(yearDate).add(1, 'months')
        })

        return result
      },
      daysInMonth () {
        let self = this
        let monthValue = _.parseInt(self.momentArguments.mm) - 1
        return self.daysInMonthsOfYear[monthValue]
      },
      formatData () {
        let self = this
        let datePartsCollection = []

        _.forEach(self.orderByFormat, segment => {
          switch (segment) {
            case 'dd':
              datePartsCollection.push('dd')
              break
            case 'mm':
              datePartsCollection.push('mm')
              break
            case 'yyyy':
              datePartsCollection.push('yyyy')
              break
            default:
              return null
          }
        })

        return datePartsCollection.join('-')
      },
      isFullDate () {
        let self = this
        return _.size(self.momentArguments.dd) === self.dateInputs.dd.blurPadding &&
        _.size(self.momentArguments.mm) === self.dateInputs.mm.blurPadding &&
        _.size(self.momentArguments.yyyy) === self.dateInputs.yyyy.blurPadding
      },
      momentDateValue () {
        let self = this
        let result = {}

        if (!self.isInvalid && self.isFullDate) {
          let monthValue = _.parseInt(self.momentArguments.mm) - 1
          if (!Number.isNaN(monthValue) && monthValue >= 0) {
            let newMoment = moment().startOf('day')
            result = newMoment.set({
              'year': Number(self.momentArguments.yyyy),
              'months': Number(monthValue),
              'date': Number(self.momentArguments.dd)
            }).toObject()
          }
        }
        return result
      },
      momentArgumentsIsEmpty () {
        let self = this
        return _.size(self.momentArguments.dd) === 0 &&
        _.size(self.momentArguments.mm) === 0 &&
        _.size(self.momentArguments.yyyy) === 0
      },
      momentArgumentsHasEmpty () {
        let self = this
        return _.size(self.momentArguments.dd) === 0 ||
        _.size(self.momentArguments.mm) === 0 ||
        _.size(self.momentArguments.yyyy) === 0
      },
      isInvalid () {
        let self = this
        if (self.momentArgumentsIsEmpty) {
          return false
        }

        let monthValue = _.parseInt(self.momentArguments.mm) - 1
        let dayValue = _.parseInt(self.momentArguments.dd)
        return self.momentArgumentsHasEmpty ||
        monthValue < 0 || monthValue > 11 ||
        dayValue < 1 || dayValue > self.daysInMonth
      }
    },
    created () {
      let self = this
      if (self.value) {
        // Ensure month doesn't just start as 1 if it's null
        let monthValue = self.value.months === null ? null : self.value.months + 1

        // First initialization, needs to be set without events
        self.dateInputs.dd.value = self.value.date
        self.dateInputs.mm.value = monthValue
        self.dateInputs.yyyy.value = self.value.years
        self.momentArguments.dd = self.padValue(_.toString(self.value.date), 'dd')
        self.momentArguments.mm = self.padValue(_.toString(monthValue), 'mm')
        self.momentArguments.yyyy = self.padValue(_.toString(self.value.years), 'yyyy')
      }
    },
    methods: {
      setSegmentValue (event) {
        let self = this
        self.setSegment(self.activeSegment, event.target.value)
      },
      setSegment (segment, value) {
        let self = this
        Vue.set(self.momentArguments, segment, self.padValue(value, segment))
        self.dateInputs[segment].value = value

        if (_.isEqual(self.activeSegment, segment) && _.size(value) === self.dateInputs[segment].blurPadding) {
          self.focusNextSegment()
        }

        if (!self.isInvalid) {
          self.emitValue()
        }
      },
      addToSegment () {
        let self = this
        if (self.isInvalid) {
          if (_.parseInt(self.dateInputs.dd.value) > self.daysInMonth) {
            self.setSegment('dd', '1')
            self.setSegment('mm', _.toString(_.parseInt(self.momentArguments.mm) + 1))
          }
          if (_.parseInt(self.dateInputs.mm.value) > 12) {
            self.setSegment('mm', '1')
            self.setSegment('yyyy', _.toString(_.parseInt(self.momentArguments.yyyy) + 1))
          }
        }
      },
      subtractSegment () {
        let self = this
        if (self.isInvalid) {
          if (self.dateInputs.mm.value < 1) {
            self.setSegment('mm', '12')
            self.setSegment('yyyy', _.toString(_.parseInt(self.momentArguments.yyyy) - 1))
          }
          if (_.parseInt(self.dateInputs.dd.value) === 0) {
            self.setSegment('mm', _.toString(_.parseInt(self.momentArguments.mm) - 1))
            self.setSegment('dd', _.toString(self.daysInMonth))
          }
        }
      },
      moveSelector (event) {
        let self = this
        self.updateValue(event)
      },
      updateCaret: _.debounce(function (e) {
        let self = this
        self.updateValue(e)
      }, 200
      ),
      focusNextSegment () {
        let self = this
        switch (self.activeSegment) {
          case null:
            self.focusSegment('dd')
            break
          case 'dd':
            self.focusSegment('mm')
            break
          case 'mm':
            self.focusSegment('yyyy')
            break
          case 'yyyy':
            // maintain focus on year
            break
          default:
            break
        }
      },
      updateValue (event) {
        let self = this
        let orderKey = _.invert(self.orderByFormat)[self.activeSegment]

        switch (event.keyCode) {
          case 37:
            if (event.srcElement.selectionEnd === 0) {
              if (orderKey > 0) {
                orderKey--
              } else {
                orderKey = _.size(self.orderByFormat) - 1
              }
            }
            break
          case 39:
            if (event.srcElement.selectionEnd === _.size(event.srcElement.value)) {
              if (orderKey < 2) {
                orderKey++
              } else {
                orderKey = 0
              }
            }
            break
          default:
        // don't change focus
        }
        self.focusSegment(self.orderByFormat[orderKey])
      },
      padValue (value, segment) {
        let self = this
        if (value || value === 0) {
          return value.padStart(self.dateInputs[segment].blurPadding, 0)
        }
      },
      focusSegment (segment) {
        let self = this
        self.activeSegment = segment
        self.focused = true
        self.$refs[self.dateInputs[segment].ref][0].focus()
      },
      onBlur () {
        let self = this
        self.focused = false
        self.activeSegment = null
      },
      showingSegment (segment) {
        let self = this
        return self.showDate[self.dateInputs[segment].ref]
      },
      emitValue () {
        let self = this
        self.$emit('change', self.momentDateValue)
      },
      clearInput () {
        let self = this
        if (!self.disabled) {
          self.setSegment('dd', '')
          self.setSegment('mm', '')
          self.setSegment('yyyy', '')
        }
      }
    },
    watch: {
      value: {
        handler: function (newValue, oldValue) {
          let self = this
          if (self.showDebugLogs) {
            // console.log(newValue.date)
            // console.log(newValue.months)
            // console.log(newValue.years)
          }

          if (oldValue && newValue &&
          newValue.date === oldValue.date &&
          newValue.months === oldValue.months &&
          newValue.years === oldValue.years) {
            return
          }

          if (newValue.date) {
            // Ensure javascript doesn't delete zero-based month from existence
            let zeroFriendlyMonthValue = Number.isNaN(parseInt(newValue.months))
            ? newValue.months : parseInt(newValue.months)

            self.setSegment('dd', _.cloneDeep(newValue.date.toString()))
            // Add one to month so it's human-readable
            self.setSegment('mm', _.cloneDeep((zeroFriendlyMonthValue + 1).toString()))
            self.setSegment('yyyy', _.cloneDeep(newValue.years.toString()))
          }
        },
        deep: true
      }
    }
  }
</script>

<style lang="scss" scoped>
  /*@import "~ui.kit.styling";*/
  @import '../../uikit';

  $spacing: 0.75em;

  .FormDate_segment {
    float: left;
    margin-left: 4px;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
  }

  .FormDate {
    box-sizing: border-box;
    display: inline-flex;
    position: relative;
    overflow: hidden;
    background-color: white;
    border: 1px solid $primaryBorderColor;
    border-radius: 0.25em;
    height: 25px;
    min-width: 135px;

    &.focused {
      border-color: $pendingBlue;
    }
    &.invalid {
      border-color: $errorRed;
    }

    .clear-button {
      box-sizing: border-box;
      margin: 0;
      float: right;
      height: 100%;
      width: 24px;
      background-color: transparent;
      padding: 5px 6px 6px 6px;

      &:hover {
        background-color: $lightGreyBackground;
      }

      &:disabled {
        background-color: transparent;
        cursor: default;
      }

      .null-icon {
        height: 100%;
        width: 100%;
        box-sizing: border-box;
      }
    }

    // 1. Hide the spinner button in Chrome, Safari and Firefox.
    &__input {
      position: relative;
      box-sizing: inherit;
      height: 23px;
      padding: 0;
      border: none;
      text-align: center;
      -moz-appearance: textfield; // 1

      &::-webkit-inner-spin-button {
        display: none; // 1
      }

      &:focus {
        outline: none;
      }

      &:disabled {
        background-color: transparent;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }

      &--day,
      &--month {
        width: 27px;
      }

      &--year {
        width: 40px;
      }
    }

    &__divider {
      pointer-events: none;
      color: $mediumGreyFont;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      -khtml-user-select: none;
      -moz-user-select: none;
      -ms-user-select: none;
      user-select: none;

      &.empty {
        color: $lightGreyFont;
      }
    }
  }
</style>



// WEBPACK FOOTER //
// node_modules/pro4all-ui/sns-components/kit-components/input-templates/date/DateInput.vue